'use client';

import * as React from 'react';
import { ChatView } from '@langbridge-builder/chat-ui';
import type {
  Activity,
  ChatMessage,
  ChatPhase,
  InterruptDecision,
  PendingInterrupt,
} from '@langbridge-builder/chat-ui';
import type { Attachment } from '@langbridge-builder/design-system';
import { WebappShell } from './webapp-shell';
import type { SidebarSessionGroup } from './session-sidebar';

/** Standard S2 AI disclaimer shown in the composer footer (design S2App parity). */
export const S2_DISCLAIMER = 'LangBridge가 실수할 수 있어요. 중요한 정보는 확인하세요.';

export interface WebappSurfaceProps {
  title: React.ReactNode;
  badge?: React.ReactNode;
  headerRight?: React.ReactNode;
  activeId?: string;
  onSelect?: (id: string) => void;
  onNewChat?: () => void;
  sessionGroups?: SidebarSessionGroup[];
  onDeleteSession?: (id: string) => void;
  sessionsLoading?: boolean;
  messages: ChatMessage[];
  phase: ChatPhase;
  activity?: Activity | null;
  interrupt?: PendingInterrupt | null;
  onRespondInterrupt?: (decisions: InterruptDecision[]) => void;
  error?: string;
  onSend: (text: string, attachments?: Attachment[]) => void;
  attach?: { accept: boolean; mode?: 'file' | 'url'; mimeTypes?: string[] };
  onRetry?: () => void;
  onAbort?: () => void;
  quickReplies?: string[];
  renderA2ui?: (messages: Record<string, unknown>[]) => React.ReactNode;
  renderRichContent?: () => React.ReactNode;
}

/**
 * The one true S2 web-app composition: session sidebar + product top bar
 * (WebappShell) wrapping the centered full-mode ChatView. Every S2 route
 * (deploy preview, studio preview, standalone) renders through this so the
 * surface stays identical. Fills its parent's height — give it an h-dvh/h-full box.
 */
export function WebappSurface({
  title,
  badge,
  headerRight,
  activeId,
  onSelect,
  onNewChat,
  sessionGroups,
  onDeleteSession,
  sessionsLoading,
  messages,
  phase,
  activity,
  interrupt,
  onRespondInterrupt,
  error,
  onSend,
  attach,
  onRetry,
  onAbort,
  quickReplies,
  renderA2ui,
  renderRichContent,
}: WebappSurfaceProps) {
  return (
    <WebappShell
      title={title}
      badge={badge}
      headerRight={headerRight}
      activeId={activeId}
      onSelect={onSelect}
      onNewChat={onNewChat}
      sessionGroups={sessionGroups}
      onDeleteSession={onDeleteSession}
      sessionsLoading={sessionsLoading}
    >
      <ChatView
        mode="full"
        showHeader={false}
        poweredBy
        disclaimer={S2_DISCLAIMER}
        messages={messages}
        phase={phase}
        activity={activity}
        interrupt={interrupt}
        onRespondInterrupt={onRespondInterrupt}
        error={error}
        onSend={onSend}
        attach={attach}
        onRetry={onRetry}
        onAbort={onAbort}
        quickReplies={quickReplies}
        renderA2ui={renderA2ui}
        renderRichContent={renderRichContent}
      />
    </WebappShell>
  );
}
